/**
 * PrecisionHitTestingEngine - High-performance character positioning
 * 
 * Replaces current HitTestingEngine with optimized spatial indexing
 * Provides sub-pixel accuracy and handles complex text layouts
 * Designed for EdgeDocs legal document editing with <1ms performance target
 */

import { CharacterSpatialIndex } from '../core/CharacterSpatialIndex';
import { UnifiedCoordinateSystem, CanvasCoordinate, LayoutContext } from '../core/UnifiedCoordinateSystem';
import { CaretPerformanceMonitor } from '../monitoring/CaretPerformanceMonitor';
import { 
  HitResult, 
  CaretPosition, 
  CharacterBox,
  HitDebugInfo
} from '../interfaces/CaretInterfaces';
import { CoordinateSpace } from '../core/UnifiedCoordinateSystem';

export class PrecisionHitTestingEngine {
  private spatialIndex: CharacterSpatialIndex;
  private performanceMonitor: CaretPerformanceMonitor;
  private layoutPlanHash: string | null = null;
  private isIndexValid: boolean = false;
  private currentLayoutPlan: any = null; // Store the actual layout plan for retrieval
  
  // Performance optimization
  private hitTestCache: Map<string, HitResult> = new Map();
  private maxCacheSize: number = 1000;
  
  constructor() {
    this.spatialIndex = new CharacterSpatialIndex();
    this.performanceMonitor = new CaretPerformanceMonitor();
    
    console.log('[PrecisionHitTestingEngine] Initialized with performance monitoring');
  }
  
  // ============================================================================
  // LAYOUT PLAN MANAGEMENT
  // ============================================================================
  
  /**
   * Update spatial index when layout plan changes
   */
  updateLayoutPlan(layoutPlan: any, planHash: string): void {
    if (this.layoutPlanHash === planHash && this.isIndexValid) {
      console.log('[PrecisionHitTestingEngine] Layout plan unchanged, using cached index');
      return;
    }
    
    console.log('[PrecisionHitTestingEngine] Building new spatial index for layout plan:', planHash.slice(0, 8));
    
    const endTiming = this.performanceMonitor.startOperation('buildSpatialIndex');
    
    try {
      // Validate layout plan structure
      if (!this.validateLayoutPlan(layoutPlan)) {
        console.error('[PrecisionHitTestingEngine] Invalid layout plan structure');
        return;
      }
      
      // Build spatial index
      this.spatialIndex.buildFromLayoutPlan(layoutPlan);
      
      // Update state
      this.layoutPlanHash = planHash;
      this.isIndexValid = true;
      this.currentLayoutPlan = layoutPlan; // Store the layout plan for retrieval
      
      // Debug: Verify layout plan storage
      console.log('[PrecisionHitTestingEngine] Layout plan stored successfully:', {
        planHash: this.layoutPlanHash,
        isIndexValid: this.isIndexValid,
        hasStoredLayoutPlan: !!this.currentLayoutPlan,
        storedLayoutPlanType: typeof this.currentLayoutPlan,
        storedLayoutPlanSize: this.currentLayoutPlan ? JSON.stringify(this.currentLayoutPlan).length : 0,
        storedLayoutPlanKeys: this.currentLayoutPlan ? Object.keys(this.currentLayoutPlan) : [],
        timestamp: performance.now()
      });
      
      // Clear cache when layout changes
      this.clearHitTestCache();
      
      console.log('[PrecisionHitTestingEngine] Spatial index updated successfully');
      
    } catch (error) {
      console.error('[PrecisionHitTestingEngine] Failed to build spatial index:', error);
      this.isIndexValid = false;
    } finally {
      endTiming();
    }
  }
  
  private validateLayoutPlan(layoutPlan: any): boolean {
    console.log('[PrecisionHitTestingEngine] Validating layout plan structure:', {
      hasLayoutPlan: !!layoutPlan,
      layoutPlanKeys: layoutPlan ? Object.keys(layoutPlan) : [],
      hasPages: !!layoutPlan?.pages,
      pagesIsArray: Array.isArray(layoutPlan?.pages),
      pagesLength: layoutPlan?.pages?.length || 0,
      firstPageKeys: layoutPlan?.pages?.[0] ? Object.keys(layoutPlan.pages[0]) : [],
      hasFrames: !!layoutPlan?.pages?.[0]?.frames,
      framesIsArray: Array.isArray(layoutPlan?.pages?.[0]?.frames),
      firstFrameKeys: layoutPlan?.pages?.[0]?.frames?.[0] ? Object.keys(layoutPlan.pages[0].frames[0]) : [],
      hasLines: !!layoutPlan?.pages?.[0]?.frames?.[0]?.lines,
      linesIsArray: Array.isArray(layoutPlan?.pages?.[0]?.frames?.[0]?.lines)
    });
    
    if (!layoutPlan) {
      console.error('[PrecisionHitTestingEngine] Validation failed: No layout plan');
      return false;
    }
    if (!layoutPlan.pages || !Array.isArray(layoutPlan.pages)) {
      console.error('[PrecisionHitTestingEngine] Validation failed: No pages array');
      return false;
    }
    if (!layoutPlan.pages[0]) {
      console.error('[PrecisionHitTestingEngine] Validation failed: No first page');
      return false;
    }
    if (!layoutPlan.pages[0].frames || !Array.isArray(layoutPlan.pages[0].frames)) {
      console.error('[PrecisionHitTestingEngine] Validation failed: No frames array');
      return false;
    }
    if (!layoutPlan.pages[0].frames[0]) {
      console.error('[PrecisionHitTestingEngine] Validation failed: No first frame');
      return false;
    }
    if (!layoutPlan.pages[0].frames[0].lines || !Array.isArray(layoutPlan.pages[0].frames[0].lines)) {
      console.error('[PrecisionHitTestingEngine] Validation failed: No lines array');
      return false;
    }
    
    console.log('[PrecisionHitTestingEngine] Layout plan validation PASSED');
    return true;
  }
  
  // ============================================================================
  // HIT TESTING API
  // ============================================================================
  
  /**
   * Primary hit testing method - O(log n) performance
   */
  hitTest(canvasX: number, canvasY: number, context?: LayoutContext): HitResult {
    const endTiming = this.performanceMonitor.startOperation('hitTest');
    
    try {
      // Check cache first
      const cacheKey = `${canvasX.toFixed(1)},${canvasY.toFixed(1)}`;
      if (this.hitTestCache.has(cacheKey)) {
        this.performanceMonitor.recordCacheHit(true);
        const cachedResult = this.hitTestCache.get(cacheKey)!;
        console.log('💾 [PrecisionHitTestingEngine] Cache hit for position:', { canvasX, canvasY });
        return cachedResult;
      }
      
      this.performanceMonitor.recordCacheHit(false);
      
      // Validate input coordinates
      const canvasCoord = UnifiedCoordinateSystem.toCanvas(
        { x: canvasX, y: canvasY, space: CoordinateSpace.CANVAS },
        context || this.getDefaultLayoutContext()
      );
      
      if (context) {
        const validation = UnifiedCoordinateSystem.validateCanvas(canvasCoord, context);
        if (!validation.isValid) {
          console.warn('[PrecisionHitTestingEngine] Invalid coordinates:', validation.errors);
        }
      }
      
      // Perform hit testing
      const result = this.performHitTest(canvasCoord);
      
      // Cache high-confidence results
      if (result.confidence > 0.8) {
        this.addToCache(cacheKey, result);
      }
      
      return result;
      
    } finally {
      endTiming();
    }
  }
  
  /**
   * Batch hit testing for selection ranges
   */
  hitTestRange(
    startX: number, startY: number, 
    endX: number, endY: number,
    sampleCount: number = 20
  ): HitResult[] {
    const endTiming = this.performanceMonitor.startOperation('hitTestRange');
    
    try {
      const results: HitResult[] = [];
      
      // Sample points along the selection path
      for (let i = 0; i <= sampleCount; i++) {
        const progress = i / sampleCount;
        const x = startX + (endX - startX) * progress;
        const y = startY + (endY - startY) * progress;
        
        results.push(this.hitTest(x, y));
      }
      
      console.log(`[PrecisionHitTestingEngine] Range hit test completed with ${results.length} samples`);
      return results;
      
    } finally {
      endTiming();
    }
  }
  
  /**
   * Get character position by index (for caret rendering)
   */
  getCharacterPosition(index: number): CanvasCoordinate | null {
    if (!this.isIndexValid) {
      console.warn('[PrecisionHitTestingEngine] Index not valid, cannot get character position');
      return null;
    }
    
    const endTiming = this.performanceMonitor.startOperation('getCharacterPosition');
    
    try {
      return this.spatialIndex.getCharacterPosition(index);
    } finally {
      endTiming();
    }
  }
  
  // ============================================================================
  // CORE HIT TESTING LOGIC
  // ============================================================================
  
  private performHitTest(canvasCoord: CanvasCoordinate): HitResult {
    if (!this.isIndexValid) {
      return this.createFallbackResult(canvasCoord, 'Index not built');
    }
    
    const startTime = performance.now();
    
    // Use spatial index for O(log n) lookup
    const characterBox = this.spatialIndex.hitTest(canvasCoord.x, canvasCoord.y);
    
    if (characterBox) {
      // Exact hit found
      const position = this.createCaretPosition(characterBox, canvasCoord);
      const hitTime = performance.now() - startTime;
      
      return {
        position,
        confidence: 1.0,
        method: 'exact',
        debugInfo: {
          hitTime: `${hitTime.toFixed(3)}ms`,
          searchMethod: 'spatial_index',
          candidatesChecked: 1,
          characterBox: [
            characterBox.bounds.x,
            characterBox.bounds.y,
            characterBox.bounds.width,
            characterBox.bounds.height
          ]
        }
      };
    }
    
    // No exact hit - this should be rare with good spatial indexing
    console.warn('[PrecisionHitTestingEngine] No character found at position:', { 
      x: canvasCoord.x, 
      y: canvasCoord.y 
    });
    
    return this.createFallbackResult(canvasCoord, 'No character at position');
  }
  
  private createCaretPosition(characterBox: CharacterBox, clickCoord: CanvasCoordinate): CaretPosition {
    return {
      index: characterBox.index,
      coordinates: {
        x: characterBox.bounds.x,
        y: characterBox.bounds.y + characterBox.bounds.height * 0.8, // Baseline
        space: CoordinateSpace.CANVAS,
        precision: 'subpixel',
        timestamp: performance.now()
      },
      lineIndex: characterBox.lineIndex,
      runIndex: characterBox.runIndex,
      charInRun: characterBox.charInRun,
      context: {
        isWordStart: characterBox.metadata.isWordStart,
        isParagraphStart: characterBox.metadata.isParagraphStart,
        isLineStart: characterBox.metadata.isLineStart,
        isWhitespace: characterBox.metadata.unicodeCategory === 'Whitespace',
        isPunctuation: characterBox.metadata.unicodeCategory === 'Punctuation',
        isClusterStart: characterBox.metadata.isClusterStart,
        isRTL: characterBox.metadata.isRTL
      },
      metadata: {
        method: 'exact',
        confidence: 1.0,
        timing: {
          totalTime: performance.now() - (clickCoord.timestamp || performance.now())
        },
        debugInfo: {
          clickCoordinates: { x: clickCoord.x, y: clickCoord.y },
          characterBox: [
            characterBox.bounds.x,
            characterBox.bounds.y,
            characterBox.bounds.width,
            characterBox.bounds.height
          ]
        }
      }
    };
  }
  
  private createFallbackResult(canvasCoord: CanvasCoordinate, reason: string): HitResult {
    return {
      position: {
        index: 0,
        coordinates: canvasCoord,
        lineIndex: 0,
        runIndex: 0,
        charInRun: 0,
        context: {},
        metadata: {
          method: 'fallback',
          confidence: 0.1,
          timing: { totalTime: 0 },
          debugInfo: { fallbackReason: reason }
        }
      },
      confidence: 0.1,
      method: 'fallback',
      debugInfo: {
        hitTime: '0ms',
        searchMethod: 'fallback',
        candidatesChecked: 0,
        fallbackReason: reason
      }
    };
  }
  
  // ============================================================================
  // CACHE MANAGEMENT
  // ============================================================================
  
  private addToCache(key: string, result: HitResult): void {
    // Implement LRU cache behavior
    if (this.hitTestCache.size >= this.maxCacheSize) {
      const firstKey = this.hitTestCache.keys().next().value;
      if (firstKey !== undefined) {
        this.hitTestCache.delete(firstKey);
      }
    }
    
    this.hitTestCache.set(key, result);
  }
  
  private clearHitTestCache(): void {
    this.hitTestCache.clear();
    console.log('[PrecisionHitTestingEngine] Hit test cache cleared');
  }
  
  // ============================================================================
  // UTILITIES
  // ============================================================================
  
  private getDefaultLayoutContext(): LayoutContext {
    return {
      contentRect: { x: 108, y: 108, width: 636, height: 840 },
      margins: { top: 108, right: 72, bottom: 108, left: 108 },
      dpi: 96,
      pageSize: { width: 816, height: 1056 }
    };
  }
  
  /**
   * Get system status and performance metrics
   */
  getSystemStatus(): {
    isReady: boolean;
    layoutPlanHash: string | null;
    performance: any;
    cacheStats: any;
    spatialIndexStats: any;
  } {
    return {
      isReady: this.isIndexValid,
      layoutPlanHash: this.layoutPlanHash,
      performance: this.performanceMonitor.getMetrics(),
      cacheStats: {
        size: this.hitTestCache.size,
        maxSize: this.maxCacheSize,
        hitRate: this.performanceMonitor.getMetrics().cacheHitRate
      },
      spatialIndexStats: this.spatialIndex.getPerformanceStats()
    };
  }
  
  /**
   * Validate hit testing accuracy (for testing)
   */
  validateAccuracy(testCases: Array<{
    click: { x: number; y: number };
    expectedIndex: number;
  }>): { accuracy: number; results: any[] } {
    const results: any[] = [];
    let correct = 0;
    
    for (const testCase of testCases) {
      const result = this.hitTest(testCase.click.x, testCase.click.y);
      const isCorrect = result.position.index === testCase.expectedIndex;
      
      if (isCorrect) correct++;
      
      results.push({
        input: testCase.click,
        expected: testCase.expectedIndex,
        actual: result.position.index,
        correct: isCorrect,
        confidence: result.confidence,
        method: result.method
      });
    }
    
    const accuracy = testCases.length > 0 ? correct / testCases.length : 0;
    
    console.log(`[PrecisionHitTestingEngine] Accuracy validation: ${(accuracy * 100).toFixed(1)}% (${correct}/${testCases.length})`);
    
    return { accuracy, results };
  }
  
  /**
   * Export debug information
   */
  exportDebugInfo(): any {
    return {
      system: this.getSystemStatus(),
      spatialIndex: this.spatialIndex.getDebugInfo(),
      performance: this.performanceMonitor.exportData(),
      cache: {
        size: this.hitTestCache.size,
        keys: Array.from(this.hitTestCache.keys()).slice(0, 10) // Sample of cache keys
      }
    };
  }
  
  /**
   * Get current layout plan (for caret position updates)
   */
  getCurrentLayoutPlan(): any {
    console.log('[PrecisionHitTestingEngine] getCurrentLayoutPlan() called:', {
      hasLayoutPlan: !!this.currentLayoutPlan,
      layoutPlanType: typeof this.currentLayoutPlan,
      layoutPlanHash: this.layoutPlanHash,
      isIndexValid: this.isIndexValid,
      layoutPlanSize: this.currentLayoutPlan ? JSON.stringify(this.currentLayoutPlan).length : 0,
      layoutPlanKeys: this.currentLayoutPlan ? Object.keys(this.currentLayoutPlan) : [],
      timestamp: performance.now()
    });
    
    if (this.currentLayoutPlan) {
      console.log('[PrecisionHitTestingEngine] Layout plan structure:', {
        hasPages: !!this.currentLayoutPlan.pages,
        pageCount: this.currentLayoutPlan.pages?.length || 0,
        hasPageSetup: !!this.currentLayoutPlan.pageSetup,
        hasPlanHash: !!this.currentLayoutPlan.planHash,
        planHashValue: this.currentLayoutPlan.planHash
      });
      
      if (this.currentLayoutPlan.pages?.[0]) {
        const firstPage = this.currentLayoutPlan.pages[0];
        console.log('[PrecisionHitTestingEngine] First page structure:', {
          hasFrames: !!firstPage.frames,
          frameCount: firstPage.frames?.length || 0,
          hasLines: !!firstPage.frames?.[0]?.lines,
          lineCount: firstPage.frames?.[0]?.lines?.length || 0,
          hasRuns: !!firstPage.frames?.[0]?.lines?.[0]?.runs,
          runCount: firstPage.frames?.[0]?.lines?.[0]?.runs?.length || 0
        });
      }
    }
    
    return this.currentLayoutPlan;
  }

  /**
   * Reset system state (for testing)
   */
  reset(): void {
    this.spatialIndex.clear();
    this.clearHitTestCache();
    this.performanceMonitor.reset();
    this.layoutPlanHash = null;
    this.isIndexValid = false;
    this.currentLayoutPlan = null; // Clear stored layout plan
    
    console.log('[PrecisionHitTestingEngine] System reset');
  }
}
